// Admin + candidaturas sobre lib/api (cookie httpOnly; server valida role em toda rota)
import { api, ApiError } from './api';
import type {
  ApiAdminStats,
  ApiAdminUser,
  ApiAdminNotification,
  ApiWorkApplication,
  ApiAdminPayment,
} from '../types/admin';
import type { ApiAdminBlogPost, BlogCategory } from '../types/blog';

export interface AdminUserPatch {
  role?: 'user' | 'consultant' | 'admin';
  creditsAdd?: number;
  isActive?: boolean;
}

export const adminApi = {
  // ---- stats ----
  getStats: () => api.get<{ stats: ApiAdminStats }>('/admin/stats'),

  // ---- users ----
  listUsers: (query?: { q?: string; page?: number; limit?: number }) => {
    const search = new URLSearchParams();
    if (query?.q) search.set('q', query.q);
    if (query?.page) search.set('page', String(query.page));
    if (query?.limit) search.set('limit', String(query.limit));
    const qs = search.toString();
    return api.get<{ users: ApiAdminUser[] }>(`/admin/users${qs ? `?${qs}` : ''}`);
  },
  patchUser: (userId: string, patch: AdminUserPatch) =>
    api.patch<{ user: ApiAdminUser }>(`/admin/users/${userId}`, patch),

  // ---- pagamentos (fase 2) ----
  listPayments: () => api.get<{ payments: ApiAdminPayment[] }>('/admin/payments'),

  // ---- notificações ----
  listNotifications: () => api.get<{ notifications: ApiAdminNotification[] }>('/admin/notifications'),
  markNotificationRead: (notificationId: string) =>
    api.post<{ ok: boolean }>(`/admin/notifications/${notificationId}/read`),

  // ---- candidaturas (admin) ----
  listApplications: (status?: ApiWorkApplication['status']) =>
    api.get<{ applications: ApiWorkApplication[] }>(
      `/admin/work-applications${status ? `?status=${status}` : ''}`
    ),
  approveApplication: (applicationId: string) =>
    api.post<{ temporaryPassword: string }>(`/admin/work-applications/${applicationId}/approve`),
  rejectApplication: (applicationId: string) =>
    api.patch<{ ok: boolean }>(`/admin/work-applications/${applicationId}`, { status: 'rejected' }),
  // Download do currículo como blob (cookies fluem; saveAs via object URL)
  downloadResume: async (applicationId: string, fallbackName = `resumo-${applicationId}`): Promise<void> => {
    const res = await fetch(`/api/admin/work-applications/${applicationId}/resume`, {
      credentials: 'include',
    });
    if (!res.ok) {
      throw new ApiError(res.status, await res.json().catch(() => null));
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename\*?=(?:UTF-8''|"?)([^";]+)/i.exec(disposition);
    const filename = match ? decodeURIComponent(match[1]) : fallbackName;
    const url = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  },
};

// ---- blog (admin) ----
export interface AdminBlogPostInput {
  title: string;
  content: string;
  excerpt?: string;
  imageUrl?: string;
  categoryId?: string;
  isPublished?: boolean;
}

export const adminBlogApi = {
  listPosts: () => api.get<{ posts: ApiAdminBlogPost[] }>('/admin/blog/posts'),
  createPost: (input: AdminBlogPostInput) =>
    api.post<{ post: ApiAdminBlogPost }>('/admin/blog/posts', input),
  updatePost: (postId: string, input: AdminBlogPostInput) =>
    api.put<{ post: ApiAdminBlogPost }>(`/admin/blog/posts/${postId}`, input),
  deletePost: (postId: string) => api.del<{ ok: boolean }>(`/admin/blog/posts/${postId}`),
  createCategory: (name: string) =>
    api.post<{ category: BlogCategory }>('/admin/blog/categories', { name }),
};

// ---- candidatura pública (/api/work-applications, multipart 'resume' opcional) ----
export interface WorkApplicationSubmit {
  name: string;
  email: string;
  phone?: string;
  experience?: string;
  motivation?: string;
  specialty?: string;
  resume?: File | null;
}

export const submitWorkApplication = async (input: WorkApplicationSubmit) => {
  const form = new FormData();
  form.set('name', input.name);
  form.set('email', input.email);
  if (input.phone) form.set('phone', input.phone);
  if (input.experience) form.set('experience', input.experience);
  if (input.motivation) form.set('motivation', input.motivation);
  if (input.specialty) form.set('specialty', input.specialty);
  if (input.resume) form.set('resume', input.resume);
  return api.postForm<{ application: ApiWorkApplication }>('/work-applications', form);
};